/**
 * ImageCompare - Container component for comparing two images
 *
 * Provides mode switching between slider and split views.
 * Includes version selection dropdowns and responsive layout.
 *
 * Feature: 081-photo-versioning (T055, T059, T061)
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { SliderComparison } from './SliderComparison';
import { SplitComparison } from './SplitComparison';
import { FullscreenContainer } from '@/components/FullscreenContainer/FullscreenContainer';
import { FullscreenIcon } from '@/components/icons/FullscreenIcon';
import { ExitFullscreenIcon } from '@/components/icons/ExitFullscreenIcon';
import logger from '@/utils/logger';
import type { PhotoVersion } from '@/hooks/graphql/usePhotoVersionsGraphQL';
import type { AdaptivePhotoProps } from '@/components/photos/AdaptivePhoto';

/** Comparison display mode */
export type ComparisonMode = 'slider' | 'side-by-side' | 'top-bottom';

/** Photo object type compatible with AdaptivePhoto */
export type ComparePhoto = AdaptivePhotoProps['photo'];

export interface ImageCompareProps {
  /** Available versions to compare */
  versions: PhotoVersion[];
  /** Initially selected "before" version ID */
  initialBeforeId?: string;
  /** Initially selected "after" version ID */
  initialAfterId?: string;
  /** Initial comparison mode */
  initialMode?: ComparisonMode;
  /** Optional className */
  className?: string;
  /** Callback when comparison changes */
  onComparisonChange?: (beforeId: string, afterId: string) => void;
}

/**
 * Convert PhotoVersion to AdaptivePhoto-compatible photo object
 * PhotoVersion now includes variants array directly from the API
 */
function versionToPhoto(version: PhotoVersion): ComparePhoto {
  return {
    id: version.id,
    variants: version.variants,
    description: version.notes,
  };
}

/**
 * Format version for display in dropdown
 */
function formatVersionLabel(version: PhotoVersion): string {
  return `v${version.versionNumber}`;
}

/** Mode icons as inline SVGs */
const ModeIcons = {
  slider: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="7" y="1" width="2" height="14" rx="1" />
      <path d="M4 8L6 6V10L4 8Z" />
      <path d="M12 8L10 6V10L12 8Z" />
    </svg>
  ),
  'side-by-side': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="1" y="2" width="6" height="12" rx="1" strokeWidth="0" />
      <rect x="9" y="2" width="6" height="12" rx="1" strokeWidth="0" />
    </svg>
  ),
  'top-bottom': (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="2" y="1" width="12" height="6" rx="1" strokeWidth="0" />
      <rect x="2" y="9" width="12" height="6" rx="1" strokeWidth="0" />
    </svg>
  ),
};

/**
 * Image comparison container component
 */
export function ImageCompare({
  versions,
  initialBeforeId,
  initialAfterId,
  initialMode = 'slider',
  className,
  onComparisonChange,
}: ImageCompareProps) {
  const [beforeId, setBeforeId] = useState(initialBeforeId || '');
  const [afterId, setAfterId] = useState(initialAfterId || '');
  const [mode, setMode] = useState<ComparisonMode>(initialMode);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Ref for fullscreen container
  const containerRef = useRef<HTMLDivElement>(null);

  // T061: Auto-switch to top-bottom for narrow viewports
  const [isNarrow, setIsNarrow] = useState(false);

  // Handle fullscreen toggle
  const handleFullscreenToggle = useCallback(() => {
    if (isFullscreen) {
      // Exit fullscreen
      setIsFullscreen(false);
    } else {
      // Enter fullscreen - must be synchronous during user gesture
      if (containerRef.current && document.fullscreenEnabled) {
        containerRef.current.requestFullscreen().catch((error) => {
          logger.error('ImageCompare', 'Failed to enter fullscreen', error);
        });
        setIsFullscreen(true);
      }
    }
  }, [isFullscreen]);

  // Handle fullscreen exit callback
  const handleFullscreenExit = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Initialize IDs when versions load (handles async loading)
  useEffect(() => {
    if (versions.length >= 2) {
      // Only set if not already set (or if current selection is invalid)
      if (!beforeId || !versions.find(v => v.id === beforeId)) {
        // Default before to oldest version (last in array, sorted by version_number ASC)
        setBeforeId(versions[versions.length - 1].id);
      }
      if (!afterId || !versions.find(v => v.id === afterId)) {
        // Default after to newest version (first in array)
        setAfterId(versions[0].id);
      }
    }
  }, [versions, beforeId, afterId]);

  useEffect(() => {
    const checkWidth = () => {
      setIsNarrow(window.innerWidth < 640);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  // Effective mode (auto-switch for narrow viewports)
  const effectiveMode = isNarrow && mode === 'side-by-side' ? 'top-bottom' : mode;

  // Get version objects
  const beforeVersion = versions.find(v => v.id === beforeId);
  const afterVersion = versions.find(v => v.id === afterId);

  // Convert versions to photo objects for AdaptivePhoto (memoized)
  const beforePhoto = useMemo(
    () => beforeVersion ? versionToPhoto(beforeVersion) : null,
    [beforeVersion]
  );
  const afterPhoto = useMemo(
    () => afterVersion ? versionToPhoto(afterVersion) : null,
    [afterVersion]
  );

  // Notify parent of changes
  useEffect(() => {
    if (beforeId && afterId) {
      onComparisonChange?.(beforeId, afterId);
    }
  }, [beforeId, afterId, onComparisonChange]);

  // Don't render if less than 2 versions
  if (versions.length < 2) {
    return (
      <div className={cn('flex items-center justify-center p-8 text-muted', className)}>
        At least 2 versions are required to compare
      </div>
    );
  }

  return (
    <FullscreenContainer
      ref={containerRef}
      isActive={isFullscreen}
      onExit={handleFullscreenExit}
      className={cn(
        'flex flex-col',
        isFullscreen ? 'p-4' : 'gap-4',
        className
      )}
    >
      {/* Controls bar */}
      <div className={cn(
        'flex flex-wrap items-center gap-3 p-2 rounded-lg',
        isFullscreen ? 'bg-surface-1/90 backdrop-blur-sm' : 'bg-surface-1'
      )}>
        {/* Mode switcher - icons only */}
        <div className="flex bg-surface-2 rounded-md overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('slider')}
            className={cn(
              'p-2 transition-colors',
              mode === 'slider'
                ? 'bg-primary text-white'
                : 'hover:bg-surface-3'
            )}
            title="Slider comparison"
            aria-label="Slider comparison mode"
          >
            {ModeIcons.slider}
          </button>
          <button
            type="button"
            onClick={() => setMode('side-by-side')}
            className={cn(
              'p-2 transition-colors',
              mode === 'side-by-side'
                ? 'bg-primary text-white'
                : 'hover:bg-surface-3'
            )}
            title="Side by side comparison"
            aria-label="Side by side comparison mode"
          >
            {ModeIcons['side-by-side']}
          </button>
          <button
            type="button"
            onClick={() => setMode('top-bottom')}
            className={cn(
              'p-2 transition-colors',
              mode === 'top-bottom'
                ? 'bg-primary text-white'
                : 'hover:bg-surface-3'
            )}
            title="Top/bottom comparison"
            aria-label="Top/bottom comparison mode"
          >
            {ModeIcons['top-bottom']}
          </button>
        </div>

        {/* T059: Version selectors - compact, no labels */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Before version selector */}
          <select
            id="before-version"
            value={beforeId}
            onChange={(e) => setBeforeId(e.target.value)}
            className="px-2 py-1.5 text-sm bg-surface-2 border border-border rounded min-w-[4rem]"
            title="Before version"
            aria-label="Select before version"
          >
            {versions.map(version => (
              <option
                key={version.id}
                value={version.id}
                disabled={version.id === afterId}
              >
                {formatVersionLabel(version)}
              </option>
            ))}
          </select>

          {/* Swap button */}
          <button
            type="button"
            onClick={() => {
              const temp = beforeId;
              setBeforeId(afterId);
              setAfterId(temp);
            }}
            className="px-2 py-1.5 text-sm bg-surface-2 border border-border rounded hover:bg-surface-3 transition-colors"
            title="Swap before and after"
            aria-label="Swap versions"
          >
            ⇄
          </button>

          {/* After version selector */}
          <select
            id="after-version"
            value={afterId}
            onChange={(e) => setAfterId(e.target.value)}
            className="px-2 py-1.5 text-sm bg-surface-2 border border-border rounded min-w-[4rem]"
            title="After version"
            aria-label="Select after version"
          >
            {versions.map(version => (
              <option
                key={version.id}
                value={version.id}
                disabled={version.id === beforeId}
              >
                {formatVersionLabel(version)}
              </option>
            ))}
          </select>

          {/* Fullscreen toggle button */}
          <button
            type="button"
            onClick={handleFullscreenToggle}
            className={cn(
              'p-1.5 rounded transition-colors',
              isFullscreen
                ? 'bg-primary text-white'
                : 'bg-surface-2 border border-border hover:bg-surface-3'
            )}
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}
          >
            {isFullscreen ? (
              <ExitFullscreenIcon size={16} />
            ) : (
              <FullscreenIcon size={16} />
            )}
          </button>
        </div>
      </div>

      {/* Comparison view */}
      <div className={cn(
        'flex-1 min-h-0 rounded-lg overflow-hidden',
        isFullscreen ? 'bg-black mt-4' : 'bg-surface-2'
      )}>
        {effectiveMode === 'slider' && beforePhoto && afterPhoto && beforeVersion && afterVersion && (
          <SliderComparison
            beforePhoto={beforePhoto}
            afterPhoto={afterPhoto}
            beforeAlt={`Version ${beforeVersion.versionNumber}`}
            afterAlt={`Version ${afterVersion.versionNumber}`}
            beforeLabel={formatVersionLabel(beforeVersion)}
            afterLabel={formatVersionLabel(afterVersion)}
            className="w-full h-full"
          />
        )}
        {(effectiveMode === 'side-by-side' || effectiveMode === 'top-bottom') && beforePhoto && afterPhoto && beforeVersion && afterVersion && (
          <SplitComparison
            beforePhoto={beforePhoto}
            afterPhoto={afterPhoto}
            beforeAlt={`Version ${beforeVersion.versionNumber}`}
            afterAlt={`Version ${afterVersion.versionNumber}`}
            beforeLabel={formatVersionLabel(beforeVersion)}
            afterLabel={formatVersionLabel(afterVersion)}
            direction={effectiveMode === 'side-by-side' ? 'horizontal' : 'vertical'}
            className="w-full h-full"
          />
        )}
      </div>
    </FullscreenContainer>
  );
}

export default ImageCompare;
